import type { Appointment, Service } from '../types';

/**
 * Health Surveillance is staff-bookable only — self-service surfaces (public
 * booking flow, company portal) must not offer it. Same name-based match the
 * server uses in bookings/intake/create.js. The name is also all a certificate
 * result row carries (names are snapshotted, ids are not re-joined), hence the
 * name-only variant.
 */
export const isHealthSurveillanceName = (name: string): boolean =>
  String(name || '')
    .toLowerCase()
    .includes('health surveillance');

export const isHealthSurveillanceService = (service: Pick<Service, 'name'>): boolean =>
  isHealthSurveillanceName(service.name);

/** All primary service ids on an appointment, tolerating pre-migration rows. */
export const getAppointmentServiceIds = (app: Appointment): string[] =>
  Array.isArray(app.serviceIds) && app.serviceIds.length > 0
    ? app.serviceIds
    : app.serviceId
      ? [app.serviceId]
      : [];

/**
 * Display name for an appointment's services ("SCW + Health Surveillance").
 * Falls back to the server-enriched serviceName (already a joined list for
 * staff endpoints) and finally the raw id.
 */
export const getAppointmentServiceNames = (
  app: Appointment,
  servicesById: Map<string, Service>
): string => {
  const names = getAppointmentServiceIds(app)
    .map((id) => servicesById.get(id)?.name)
    .filter((name): name is string => Boolean(name));
  if (names.length > 0) return names.join(' + ');
  return app.serviceName || app.serviceId || '';
};
